/**
 * TaskTypesStep — Step 2 of the Job Management Wizard
 *
 * Simplified: name + attached forms via task_form table with sequence ordering.
 */

import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { FileText, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useDeleteTaskType, useTaskTypes } from '../hooks/useJobManagement';
import { EditTaskTypeDialog } from './EditTaskTypeDialog';

type DialogState =
  | { mode: 'closed' }
  | { mode: 'create' }
  | { mode: 'edit'; id: number; initialName: string };

// ---------------------------------------------------------------------------
// Main component — owns the table and delete confirmation only
// ---------------------------------------------------------------------------

export default function TaskTypesStep() {
  const { data, isLoading } = useTaskTypes();
  const deleteMutation = useDeleteTaskType();

  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState<DialogState>({ mode: 'closed' });
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  const taskTypes = data?.data ?? [];

  const filtered = useMemo(() => {
    if (!search) return taskTypes;
    const q = search.toLowerCase();
    return taskTypes.filter((t) => t.name?.toLowerCase().includes(q));
  }, [taskTypes, search]);

  const taskFormDisplayMap = useMemo(() => {
    const map = new Map<number, string[]>();
    for (const tt of taskTypes) {
      if (tt.formAssignment) {
        map.set(
          tt.id,
          tt.formAssignment
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean),
        );
      }
    }
    return map;
  }, [taskTypes]);

  async function handleDelete(id: number) {
    await deleteMutation.mutateAsync(id);
    setDeleteConfirmId(null);
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search task types..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button onClick={() => setDialog({ mode: 'create' })}>
          <Plus className="w-4 h-4 mr-2" />
          Add Task Type
        </Button>
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Assigned Forms</TableHead>
              <TableHead className="text-center w-[80px]">System</TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                  Loading...
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                  {search ? 'No task types match your search' : 'No task types found'}
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((tt) => {
                const formNames = taskFormDisplayMap.get(tt.id) ?? [];
                return (
                  <TableRow key={tt.id}>
                    <TableCell className="font-medium">{tt.name ?? '-'}</TableCell>
                    <TableCell>
                      {formNames.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {formNames.map((formName, idx) => (
                            <Badge key={formName} variant="outline" className="text-xs gap-1">
                              <span className="text-muted-foreground">{idx + 1}.</span>
                              <FileText className="w-3 h-3" />
                              {formName}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">No forms</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {tt.isSystem ? (
                        <Badge variant="secondary" className="text-xs">
                          System
                        </Badge>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            setDialog({ mode: 'edit', id: tt.id, initialName: tt.name ?? '' })
                          }
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteConfirmId(tt.id)}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Add/Edit Dialog */}
      <EditTaskTypeDialog
        mode={dialog.mode}
        editingId={dialog.mode === 'edit' ? dialog.id : null}
        initialName={dialog.mode === 'edit' ? dialog.initialName : ''}
        onClose={() => setDialog({ mode: 'closed' })}
      />

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirmId !== null} onOpenChange={() => setDeleteConfirmId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Task Type</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete this task type? This action can be reversed by an
            administrator.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
